
import { Component, Ref, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { fromNow } from '@hokify/shared-components/lib/helpers/datehelpers/from-now';
import { shortenString } from '@hokify/shared-components/lib/helpers/stringhelper';
import type {
	IAPIPageSettings,
	IAPIPageJobDetail,
	IAPIMatchForJobSeekerListEntry,
	IAPIJobPreviewForUser,
	IAPIOtherTerms
} from '@hokify/common';
import { getHeadFromPageSettings, getSocialMediaMetaData } from '~/helpers/metadata';
import type { IJobalarmData, IRecentJobsearch, ISendjobData } from '~/store/types';
import { PageNotFoundError } from '~/errors/PageNotFoundError';
import { ServerError } from '~/errors/ServerError';
import JobDetail from '@hokify/shared-components/lib/components/JobDetail.vue';
import {
	locationString,
	saveToRecentJobSearches
} from '@hokify/shared-components/lib/helpers/jobsearch';
import PopupHandler from '~/components/website/PopupHandler.vue';
import { IAPILoginUser, IAPIMatchForJobSeeker } from '@hokify/common';
import { GoneError } from '~/errors/GoneError';
import SeoFooter, { ISeoFooterData } from '~/components/website/footer/SeoFooter.vue';
import { lsTest } from '@hokify/shared-components/lib/helpers/localstorage';

const valuesStore = namespace('values');
const jobsStore = namespace('jobs');
const loginStore = namespace('login');
const relationsStore = namespace('relations');
const matchesStore = namespace('matches');
const gncStore = namespace('gnc');
const userStore = namespace('user/profile');
const landingPageStore = namespace('user/landingpage');

@Component({
	name: 'JobDetailPage',
	layout: 'plain-user',
	components: {
		JobDetail,
		SeoFooter,
		Autocomplete: () => import('@hokify/shared-components/lib/components/Autocomplete.vue'),
		LocationAutocomplete: () =>
			import('@hokify/shared-components/lib/components/LocationAutocomplete.vue'),
		CallToAction: () => import('@hokify/shared-components/lib/components/CallToAction.vue'),
		PopupHandler,
		JobPreFooter: () => import('~/components/website/jobs/JobPreFooter.vue')
	},
	scrollToTop: true,
	head(this: JobDetailPage) {
		const result = getHeadFromPageSettings(this.pageSettings);

		result.meta.push({
			hid: 'description',
			name: 'description',
			content: this.pageDescription
		});

		result.meta.push(
			...getSocialMediaMetaData(
				result,
				this.$store.state.topLevelDomain,
				[this.myJob.images?.medium],
				`hokify://job/${this.myJob.jobNr}`,
				this.$route.query,
				undefined,
				shortenString(this.myJob?.name || '', 60),
				shortenString(this.socialsDescription, 155)
			)
		);

		result.title = this.pageTitle;
		result.titleTemplate = '%s | hokify';

		return result;
	},
	async asyncData({ error, params, query, redirect, store }) {
		if (params.uref && params.job) {
			redirect(301, `/job/${params.job}`, query);
		}

		try {
			const result: IAPIPageJobDetail = await store.dispatch('jobs/loadJobDetail', {
				jobNr: params.job,
				previewCode: query.previewCode
			});

			// updateCustomBackLinkUrl depends on customBackLinkUrl from loadJobDetail call
			const customBackLinkUrl = decodeURI(
				result.additionalSettings?.customBackLink?.url || '/jobs'
			);
			await store.dispatch('jobs/updateCustomBackLinkUrl', customBackLinkUrl);

			const numberOfSimilarJobs = result.additionalSettings?.customBackLink?.results || '';
			if (numberOfSimilarJobs) {
				await store.dispatch('jobs/updateNumberOfSimilarJobs', numberOfSimilarJobs);
			}

			if (query.jobalarmid && query.emaildoubleoptin) {
				store.dispatch('jobalarm/verifyEmail', {
					emaildoubleoptin: query.emaildoubleoptin,
					jobalarmId: query.jobalarmid
				});
			}

			await store.dispatch('user/landingpage/loadLandingPageData');

			return {
				initialBranch: result.additionalSettings?.customBackLink?.branch || '',
				initialCity: result.additionalSettings?.customBackLink?.location || '',
				branch: result.additionalSettings?.customBackLink?.branch || '',
				city: result.additionalSettings?.customBackLink?.location || '',
				pageSettings: result.pageSettings || {},
				additionalSettings: result.additionalSettings,
				additionalJobData: result.additionalJobData,
				myJob: result.job,
				similarJobs: Object.freeze(result.similarJobs),
				jobOverviewLps: result.jobOverviewLps,
				lpId: result.additionalSettings?.customBackLink?.lpId || '',
				preview: !!query.previewCode && !result.job.internal?.isPublished,
				customBackLinkUrl,
				hasUTMSource: !!query.utm_source,
				numberOfSimilarJobs,
				countryCode: result.job?.location?.countryCode || 'at'
			};
		} catch (err: any) {
			if (err.response?.status === 410 && err.response.data?.redirectUrl) {
				redirect(301, `${err.response.data.redirectUrl}#jobgone`);
			} else if (err.response?.status === 410) {
				error(new GoneError('Die Stelle ist nicht mehr verfügbar.'));
			} else if (err.response?.status === 404 || err.response?.status === 422) {
				error(new PageNotFoundError('Die Stelle konnte leider nicht gefunden werden.'));
			} else if (err.response?.status === 500) {
				error(new ServerError());
			} else if (err.response) {
				error({
					statusCode: err.response.status,
					message:
						err.response.data && err.response.data.code
							? `${err.response.data.code}: ${err.response.data.msg || err.response.data.message}`
							: `Es ist ein Fehler aufgetreten${
									err.response.data ? ` (${JSON.stringify(err.response.data)})` : ''
								}`
				});
			} else {
				error(err);
			}
		}
	}
})
export default class JobDetailPage extends Vue {
	@valuesStore.Action('ontologySuggestion') ontologySuggestion;

	@jobsStore.Action('updateCustomBackLinkUrl') updateCustomBackLinkUrl;

	@jobsStore.Action('loadJobDetail') loadJobDetail;

	@relationsStore.Action('saveRelation') saveRelation;

	@relationsStore.Action('discardRelation') discardRelation;

	@matchesStore.Getter('getMatchByRelation') getMatchByRelation;

	@matchesStore.Getter('getStatusOfRelation') getStatusOfRelation;

	@matchesStore.Action('deleteMatch') deleteMatch;

	@loginStore.State('loggedIn') loggedIn;

	@matchesStore.Action('getSaved') getSaved!: (
		forceReload?: boolean
	) => Promise<IAPIMatchForJobSeekerListEntry[]>;

	@matchesStore.Getter('getAll') savedMatches: IAPIMatchForJobSeekerListEntry[] | undefined;

	@Ref('popupHandler') readonly popupHandler!: any;

	@gncStore.Action('sendGnc') sendGnc;

	@userStore.State('obj') user!: IAPILoginUser;

	@landingPageStore.State('popularLandingPages') popularLandingPages;

	@jobsStore.State('otherTerms') otherTerms?: IAPIOtherTerms;

	error: { title: string; text: string } | boolean = false;

	mode = 'sendjob';

	additionalJobData!: IAPIPageJobDetail['additionalJobData'];

	additionalSettings!: IAPIPageJobDetail['additionalSettings'];

	currentEmail = '';

	customBackLinkUrl!: string;

	jobOverviewLps: IAPIPageJobDetail['jobOverviewLps'] = [];

	lpId!: string;

	branch!: string;

	initialBranch!: string;

	initialCity!: string;

	countryCode!: string;

	city!: string;

	hasUTMSource!: boolean;

	myJob: any = {};

	observer?: IntersectionObserver;

	pageSettings!: IAPIPageSettings;

	similarJobs: IAPIPageJobDetail['similarJobs'] = [];

	showBar = false;

	fromNow = fromNow;

	preview!: boolean;

	cordova: string | null = process.env.cordova || null;

	numberOfSimilarJobs = '';

	locationString = locationString;

	gncCalculation: {} | undefined;

	match?: IAPIMatchForJobSeeker;

	recentJobSearches: IRecentJobsearch[] = [];

	data() {
		return { gncCalculation: undefined };
	}

	winLoad(callback) {
		if (document.readyState === 'complete') {
			callback();
		} else {
			window.addEventListener('load', callback);
		}
	}

	async mounted() {
		const country = this.myJob.location?.countryCode
			? this.myJob.location.countryCode.replace(/\W/g, '').toUpperCase()
			: undefined;

		this.$trackWebsiteEvent('job_view', {
			name: this.myJob.name,
			jobNr: this.myJob.jobNr,
			jobId: this.myJob._id,
			topfields: this.myJob.rootFields,
			jobfields: this.myJob.fields.map(f => f.name),
			type: this.myJob.type,
			similarJobs: this.similarJobs?.map(j => ({ _id: j?.job?._id })),
			priorize: this.myJob.internal.trackingValue || 1,
			country,
			city: this.myJob.location?.city || undefined,
			is_logged_in: this.$store.state.login.loggedIn ? 1 : 0
		});

		this.registerIntersectionObserver();

		if (this.hasUTMSource) {
			const newSearch = {
				linkUrl: this.customBackLinkUrl,
				title: this.recentJobSearchTitle(),
				recordCount: 0
			};
			saveToRecentJobSearches(newSearch);
		}

		if (lsTest()) {
			const storage = lsTest() && localStorage.getItem('recent-jobsearches');
			if (typeof storage === 'string') {
				this.recentJobSearches = JSON.parse(storage);
			}
		}
	}

	recentJobSearchTitle(): string {
		let jobSearchTitle = '';
		jobSearchTitle += this.initialBranch ? this.initialBranch : 'Jobs';
		if (this.initialCity) {
			jobSearchTitle += ` in ${this.initialCity}`;
		}
		return jobSearchTitle;
	}

	beforeDestroy() {
		this.observer?.disconnect();
		this.updateCustomBackLinkUrl('');
	}

	registerIntersectionObserver() {
		this.observer = new IntersectionObserver(this.isVisible);
		this.observeElements();
	}

	observeElements() {
		const el1 =
			document.querySelector('#jobdetail-apply-mobile') ||
			document.querySelector('#jobdetail-chat-mobile') ||
			document.querySelector('#jobdetail-contact-mobile');

		if (el1) {
			this.observer?.observe(el1);
		}
		const el2 = document.querySelector('#footer');
		if (el2) {
			this.observer?.observe(el2);
		}
	}

	isVisible(entries) {
		this.showBar = !entries.some(entry => entry.isIntersecting);
	}

	get pageTitle(): string {
		let pageTitle;
		const job = this.myJob;
		const jobTitle = job?.name.replace('(m/w/d)', '');
		const companyName = job?.company?.name.replace(/\s*\[\[.*?\]\]/g, '');

		const titleAndCompanyLength = jobTitle.length + companyName.length;

		if (titleAndCompanyLength < 43) {
			pageTitle = `${jobTitle} ${companyName ? `bei ${companyName}` : ''} ${
				job?.type ? `| ${job.type}` : ''
			}`;
		} else {
			pageTitle = `${jobTitle} ${companyName ? `bei ${companyName}` : ''}`;
		}
		return pageTitle;
	}

	get pageDescription(): string {
		let pageDescription;
		const job = this.myJob;
		const jobTitle = job?.name.replace('(m/w/d)', '');
		const companyName = job?.company?.name.replace(/\s*\[\[.*?\]\]/g, '');
		const titleAndCompanyLength = jobTitle.length + companyName.length;
		const gross = 'brutto/monat';
		const jobWage = job?.wage?.toLowerCase().includes(gross.toLowerCase());

		if (titleAndCompanyLength < 60) {
			pageDescription = `Offene Stelle: ${jobTitle} ${companyName ? `bei ${companyName}` : ''} ${
				job?.location?.city ? `in ${job?.location?.city}` : ''
			} gesucht. ${jobWage ? `Gehalt ab ${job.wage}` : 'Top Gehalt'} - ${
				job.type ? job.type : ''
			}. hokify - deine mobile Jobplattform`;
		} else {
			pageDescription = `Offene Stelle: ${jobTitle} ${companyName ? `bei ${companyName}` : ''} ${
				job?.location?.city ? `in ${job?.location?.city}` : ''
			} . ${jobWage ? `Gehalt ab ${job.wage}` : 'Top Gehalt'} - ${
				job.type ? job.type : ''
			}. hokify - deine Jobplattform`;
		}

		return pageDescription;
	}

	get jobalarmFormData(): IJobalarmData {
		return {
			email: this.currentEmail ? this.currentEmail : undefined,
			search: this.myJob.fields ? { searchterm: this.myJob?.fields[0]?.name } : undefined,
			location: this.initialCity ? { address: this.initialCity } : undefined,
			region: this.countryCode ? this.countryCode : 'at'
		};
	}

	get sendjobFormData(): ISendjobData {
		return {
			email: this.currentEmail || '',
			jobName: this.myJob.name,
			jobNr: this.myJob.jobNr,
			companyName: this.myJob.company?.name || ''
		};
	}

	get currentData() {
		switch (this.mode) {
			case 'sendjob':
				return this.sendjobFormData;
			case 'jobalarm':
			default:
				return this.jobalarmFormData;
		}
	}

	get currentMode() {
		return this.mode;
	}

	get linkToApply(): string {
		return `/apply/${this.myJob.jobNr}`;
	}

	get jobIsSaved() {
		return this.getStatusOfRelation({ type: 'job', obj: this.myJob }) === 'saved';
	}

	get socialsDescription(): string {
		const job = this.myJob;
		return `Job zu vergeben: ${
			job?.company?.name ? `${job.company.name.replace(/\s*\[\[.*?\]\]/g, '')} · ` : ''
		}${this.locationString(job) ? `${this.locationString(job)}` : ''}${
			job?.type ? ` · ${job.type}` : ''
		}`;
	}

	get userImage(): string | undefined {
		return this.user?.images?.small;
	}

	get activeSourcingMode(): boolean {
		return !!(this.loggedIn && this.myJob?.selectedCandidateInfo?.status === 'activesourced');
	}

	get isInContactWithCompany(): boolean {
		return !!(this.myJob?.selectedCandidateInfo?.status === 'incontact');
	}

	get mappedSeoBranches():
		| {
				title: string;
				description: string;
				to: string;
		  }[]
		| undefined {
		if (this.popularLandingPages) {
			return this.popularLandingPages.map(page => ({
				title: page.title,
				description: `${page.recordCount}`,
				to: page.linkUrl
			}));
		}
		return undefined;
	}

	get mappedSeoCities():
		| {
				title: string;
				description: string;
				to: string;
		  }[]
		| undefined {
		if (this.otherTerms?.refined) {
			return this.otherTerms?.refined.map(city => ({
				title: city.name,
				description: `${city.results}`,
				to: city.url
			}));
		}
		return undefined;
	}

	get mappedSeoRegions():
		| {
				title: string;
				description: string;
				to: string;
		  }[]
		| undefined {
		if (this.otherTerms?.places) {
			return this.otherTerms.places.map(place => ({
				title: place.name,
				description: `${place.results}`,
				to: place.url
			}));
		}
		return undefined;
	}

	get mappedSeoRecentJobSearches():
		| {
				title: string;
				description: string;
				to: string;
		  }[]
		| undefined {
		if (this.recentJobSearches) {
			return this.recentJobSearches.map(search => ({
				title: search.title,
				description: `${search.recordCount}`,
				to: search.linkUrl
			}));
		}
		return undefined;
	}

	get seoFooterData(): ISeoFooterData[] {
		return [
			{
				title: 'Jobs nach Regionen',
				id: 'regions',
				searches: this.mappedSeoRegions || []
			},
			{
				title: 'Jobs nach Städten',
				id: 'cities',
				searches: this.mappedSeoCities || []
			},
			{
				title: 'Jobs nach Branchen',
				id: 'branches',
				searches: this.mappedSeoBranches || []
			},
			{
				title: 'Deine letzten Suchen',
				id: 'job-searches',
				searches: this.mappedSeoRecentJobSearches || []
			}
		];
	}

	back(e, fallback?) {
		let hasOpener = false;
		try {
			hasOpener =
				(window?.opener?.location?.href.includes(this.$store.state.host) &&
					window.opener.location.href !== window.document.location.href) ||
				false;
		} catch (err: any) {
			// ignore access errors
		}

		if (e) {
			e.preventDefault();
		}
		if (window.history.length > 2) {
			window.history.back();
			return false;
		}
		if (hasOpener && window?.opener?.location.href) {
			window.location.href = window.opener.location.href;
			return false;
		}
		if (this.customBackLinkUrl) {
			window.location.href = this.customBackLinkUrl;
			return false;
		}
		if (!fallback) {
			return false;
		}
	}

	setupJobAlarm() {
		this.$trackWebsiteEvent('activate_job_alarm', { pageElement: 'job-detail-cta' });
		this.openForm('jobalarm');
		this.$store.commit('setJobFilter', this.jobalarmFormData);
		if (this.$askPushPermission) {
			this.$askPushPermission(true);
		}
	}

	setupSendJob(eventLabel = 'cta-text-right') {
		this.$trackWebsiteEvent('send_job', {
			pageElement: eventLabel
		});
	}

	openForm(mode: string) {
		this.mode = mode;
		this.$nextTick(() => {
			this.popupHandler.handleClick();
		});
	}

	handleJobSearch() {
		this.$router.push({
			path: '/jobs/search',
			query: { branch: this.branch, city: this.city }
		});
	}

	rowItemClicked(): void {
		this.$trackWebsiteEvent('click_job', {});
	}

	handleSendStatus(message: string, _result?: any) {
		switch (this.currentMode) {
			case 'sendjob':
				this.handleModeSendjob(message);
				break;
			case 'login':
				this.handleModeLogin(message);
				break;
			case 'jobalarm':
			default:
				this.handleModeJobalarm(message);
				break;
		}
	}

	handleModeJobalarm(message: string) {
		switch (message) {
			case 'success':
				this.$trackWebsiteEvent('activate_job_alarm_completed', {
					pageElement: 'job-detail-cta'
				});
				this.$snack.success({
					text: 'Öffne dein E-Mail Postfach und bestätige den Job-Alarm.'
				});
				break;
			case 'fail':
			default:
				this.$snack.danger({
					title: 'Fehlgeschlagen!',
					text: 'Beim Versenden des Formulars ist etwas schief gelaufen.'
				});
				break;
		}
	}

	handleModeSendjob(message: string) {
		switch (message) {
			case 'success':
				this.$trackWebsiteEvent('send_job_completed', {});
				this.$snack.success({
					text: 'Job erfolgreich zugesendet!',
					button: 'Kostenlos registrieren',
					action: () => {
						window.location.href = '/app/login';
					}
				});
				break;
			case 'fail':
			default:
				this.$snack.danger({
					title: 'Fehlgeschlagen!',
					text: 'Beim Versenden des Formulars ist etwas schief gelaufen.'
				});
				break;
		}
	}

	handleModeLogin(message: string) {
		switch (message) {
			case 'success':
				this.$snack.success({
					text: 'Erfolgreich angemeldet!'
				});
				break;
			case 'fail':
			default:
				this.$snack.danger({
					title: 'Fehlgeschlagen!',
					text: 'Beim Anmelden ist etwas schief gelaufen.'
				});
				break;
		}
	}

	async saveJob(job: IAPIJobPreviewForUser) {
		try {
			const relation = {
				type: 'job',
				obj: job
			};
			let { name } = job;
			if (name.trim().length > 30) {
				name = `${name.trim().slice(0, 30)}...`;
			}
			if (this.loggedIn) {
				if (this.getStatusOfRelation(relation) !== 'saved') {
					await this.saveRelation(relation);
					await this.$trackWebsiteEvent('save_job', {
						jobNr: job.jobNr,
						item_id: job._id,
						item_type: 'job'
					});
					this.$snack.success({
						text: `Der  Job "${name}" wurde gespeichert!`
					});
				} else if (this.getStatusOfRelation(relation) === 'saved') {
					const match = this.getMatchByRelation(relation);
					await this.deleteMatch({ match });
					this.$snack.success({
						text: `Der gespeicherte Job "${name}" wurde gelöscht!`
					});
				}
			} else {
				this.openForm('login');
				await this.$trackWebsiteEvent('save_job', {
					jobNr: job.jobNr,
					item_id: job._id,
					item_type: 'jobNotLoggedIn'
				});
			}
		} catch (err: any) {
			this.$snack.danger({
				text: 'Beim Speichern des Jobs ist ein Fehler aufgetreten. Bitte versuche es später noch einmal.'
			});
		}
	}

	async calcGnc(gnc) {
		try {
			this.gncCalculation = await this.sendGnc({ ...gnc, locationUrl: 'website-job-detail-page' });
			this.$trackWebsiteEvent('start_calculation', {});
		} catch (err: any) {
			this.$errorHandler.call(this, err);
		}
	}

	openJobsearch() {
		this.$router.push('/jobs');
		this.$trackWebsiteEvent('click_redirect', { url: '/jobs' });
	}

	async activeSourcingInvite(interaction: 'accepted' | 'declined' | 'more-info') {
		this.$trackWebsiteEvent('active_sourcing_reverse_apply', { interaction });
		if (!this.myJob?._id || !this.myJob?.jobNr) {
			this.$snack.danger({
				text: 'Es ist ein Fehler aufgetreten!'
			});
			this.$router.push('/jobs');
		}
		if (interaction === 'accepted') {
			window.location.href = `/reverse-apply/${this.myJob._id}`;
		} else if (interaction === 'declined') {
			try {
				await this.discardRelation({ type: 'job', obj: this.myJob });
				if (this.myJob?.selectedCandidateInfo?.status) {
					this.myJob.selectedCandidateInfo.status = 'request-rejected';
				}
				this.$snack.success({
					text: 'Das Unternehmen wurde über dein Nicht-Interesse informiert.'
				});
			} catch (err) {
				this.$snack.danger({
					text: 'Entscheidung konnte nicht gespeichert werden!'
				});
			}
		} else if (interaction === 'more-info') {
			window.location.href = `/more-info/${this.myJob?.jobNr}`;
		} else {
			throw new Error('interaction type missing');
		}
	}

	trackCommute(type: 'dropdown' | 'image') {
		this.$trackWebsiteEvent('click_commute_element', {
			pageElement: type
		});
	}

	newSearchViaTag(jobField: string) {
		this.$trackWebsiteEvent('click_jobdetail_tag', {});
		this.branch = jobField;
		this.handleJobSearch();
	}
}
